import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface IMEXFormData {
	tomadorNombre: string
	tomadorCuit: string
	tomadorDomicilio: string
	tomadorTelefono: string
	tomadorEmail: string
	riesgo: string
	montoGarantia: string
	montoOtro: string
	anioVigencia: string
	informacionAdicional: string
}

interface CaucionIMEXFormScreenProps {
	onNext: (data: IMEXFormData) => void
	onBack: () => void
	data?: Partial<IMEXFormData>
}

const RIESGO_OPTIONS = [
	{
		value: 'inscripcion-imex',
		label: 'Inscripción al Registro de Importadores y Exportadores',
	},
	{ value: 'despachante', label: 'Despachante de Aduanas' },
	{ value: 'agente-transporte', label: 'Agente de Transporte Aduanero' },
]

const MONTO_OPTIONS = [
	{ value: '30000', label: '$30.000' },
	{ value: '50000', label: '$50.000' },
	{ value: '100000', label: '$100.000' },
	{ value: '200000', label: '$200.000' },
	{ value: 'otro', label: 'Otro monto' },
]

export function CaucionIMEXFormScreen({
	onNext,
	onBack,
	data,
}: CaucionIMEXFormScreenProps) {
	const currentYear = new Date().getFullYear()
	const [formData, setFormData] = useState<IMEXFormData>({
		tomadorNombre: data?.tomadorNombre || '',
		tomadorCuit: data?.tomadorCuit || '',
		tomadorDomicilio: data?.tomadorDomicilio || '',
		tomadorTelefono: data?.tomadorTelefono || '',
		tomadorEmail: data?.tomadorEmail || '',
		riesgo: data?.riesgo || '',
		montoGarantia: data?.montoGarantia || '',
		montoOtro: data?.montoOtro || '',
		anioVigencia: data?.anioVigencia || String(currentYear + 1),
		informacionAdicional: data?.informacionAdicional || '',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateCUIT = (cuit: string) => {
		const cleaned = cuit.replace(/-/g, '')
		return cleaned.length === 11 && /^\d+$/.test(cleaned)
	}

	const formatCUIT = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (cleaned.length <= 2) {
			return cleaned
		} else if (cleaned.length <= 10) {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
		} else {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10, 11)}`
		}
	}

	const formatCurrency = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (!cleaned) return ''
		return Number(cleaned).toLocaleString('es-AR')
	}

	const validateEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.tomadorNombre.trim()) {
			newErrors.tomadorNombre = 'Ingresa el nombre'
		}
		if (!formData.tomadorCuit.trim()) {
			newErrors.tomadorCuit = 'Ingresa el CUIT'
		} else if (!validateCUIT(formData.tomadorCuit)) {
			newErrors.tomadorCuit = 'CUIT inválido'
		}
		if (!formData.tomadorDomicilio.trim()) {
			newErrors.tomadorDomicilio = 'Ingresa el domicilio'
		}
		if (!formData.tomadorTelefono.trim()) {
			newErrors.tomadorTelefono = 'Ingresa el teléfono'
		}
		if (!formData.tomadorEmail.trim()) {
			newErrors.tomadorEmail = 'Ingresa el email'
		} else if (!validateEmail(formData.tomadorEmail)) {
			newErrors.tomadorEmail = 'Email inválido'
		}
		if (!formData.riesgo) {
			newErrors.riesgo = 'Selecciona el riesgo a cubrir'
		}
		if (!formData.montoGarantia) {
			newErrors.montoGarantia = 'Selecciona el monto'
		}
		if (formData.montoGarantia === 'otro' && !formData.montoOtro.trim()) {
			newErrors.montoOtro = 'Ingresa el monto'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.tomadorNombre.trim() &&
		formData.tomadorCuit.trim() &&
		formData.tomadorDomicilio.trim() &&
		formData.tomadorTelefono.trim() &&
		formData.tomadorEmail.trim() &&
		formData.riesgo &&
		formData.montoGarantia &&
		(formData.montoGarantia !== 'otro' || formData.montoOtro.trim())

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos de la Solicitud
					</h2>

					<div className="flex w-full flex-col gap-4">
						<h3 className="font-parkinsans text-lg font-medium text-blue-800">
							Datos del Tomador
						</h3>
						<InputText
							label="Nombre/Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre del tomador"
							value={formData.tomadorNombre}
							onChange={(e) => {
								setFormData({ ...formData, tomadorNombre: e.target.value })
								setErrors({ ...errors, tomadorNombre: '' })
							}}
							errorText={errors.tomadorNombre}
							required
						/>
						<InputText
							label="CUIT"
							labelPosition="internal"
							size="l"
							placeholder="00-00000000-0"
							value={formData.tomadorCuit}
							onChange={(e) => {
								const formatted = formatCUIT(e.target.value)
								setFormData({ ...formData, tomadorCuit: formatted })
								setErrors({ ...errors, tomadorCuit: '' })
							}}
							errorText={errors.tomadorCuit}
							maxLength={13}
							required
						/>
						<InputText
							label="Domicilio"
							labelPosition="internal"
							size="l"
							placeholder="Dirección completa"
							value={formData.tomadorDomicilio}
							onChange={(e) => {
								setFormData({ ...formData, tomadorDomicilio: e.target.value })
								setErrors({ ...errors, tomadorDomicilio: '' })
							}}
							errorText={errors.tomadorDomicilio}
							required
						/>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<InputText
								label="Teléfono"
								labelPosition="internal"
								size="l"
								placeholder="Teléfono"
								value={formData.tomadorTelefono}
								onChange={(e) => {
									setFormData({ ...formData, tomadorTelefono: e.target.value })
									setErrors({ ...errors, tomadorTelefono: '' })
								}}
								errorText={errors.tomadorTelefono}
								required
							/>
							<InputText
								label="Email"
								labelPosition="internal"
								size="l"
								placeholder="correo@ejemplo.com"
								type="email"
								value={formData.tomadorEmail}
								onChange={(e) => {
									setFormData({ ...formData, tomadorEmail: e.target.value })
									setErrors({ ...errors, tomadorEmail: '' })
								}}
								errorText={errors.tomadorEmail}
								required
							/>
						</div>

						<div className="mt-4 border-t border-neutral-200 pt-4">
							<h3 className="font-parkinsans mb-4 text-lg font-medium text-blue-800">
								Datos de la Garantía
							</h3>

							<div className="mb-4">
								<label className="font-parkinsans mb-2 block text-sm font-medium text-neutral-700">
									Riesgo a cubrir *
								</label>
								<select
									className="font-parkinsans w-full rounded-lg border border-neutral-300 px-4 py-3 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
									value={formData.riesgo}
									onChange={(e) => {
										setFormData({ ...formData, riesgo: e.target.value })
										setErrors({ ...errors, riesgo: '' })
									}}
								>
									<option value="">Seleccionar...</option>
									{RIESGO_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
								{errors.riesgo && (
									<p className="mt-1 text-sm text-red-500">{errors.riesgo}</p>
								)}
							</div>

							<div className="mb-4">
								<label className="font-parkinsans mb-2 block text-sm font-medium text-neutral-700">
									Monto de la Garantía *
								</label>
								<select
									className="font-parkinsans w-full rounded-lg border border-neutral-300 px-4 py-3 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
									value={formData.montoGarantia}
									onChange={(e) => {
										setFormData({ ...formData, montoGarantia: e.target.value })
										setErrors({ ...errors, montoGarantia: '' })
									}}
								>
									<option value="">Seleccionar...</option>
									{MONTO_OPTIONS.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</select>
								{errors.montoGarantia && (
									<p className="mt-1 text-sm text-red-500">
										{errors.montoGarantia}
									</p>
								)}
							</div>

							{formData.montoGarantia === 'otro' && (
								<div className="mb-4">
									<InputText
										label="Especificar monto"
										labelPosition="internal"
										size="l"
										placeholder="Monto en pesos"
										value={formData.montoOtro}
										onChange={(e) => {
											const formatted = formatCurrency(e.target.value)
											setFormData({ ...formData, montoOtro: formatted })
											setErrors({ ...errors, montoOtro: '' })
										}}
										errorText={errors.montoOtro}
										required
									/>
								</div>
							)}

							<div className="mb-4">
								<InputText
									label="Vigencia hasta 31 de Julio de"
									labelPosition="internal"
									size="l"
									placeholder="Año"
									value={formData.anioVigencia}
									onChange={(e) => {
										const cleaned = e.target.value
											.replace(/\D/g, '')
											.slice(0, 4)
										setFormData({ ...formData, anioVigencia: cleaned })
									}}
									maxLength={4}
								/>
							</div>

							<div>
								<label className="font-parkinsans mb-2 block text-sm font-medium text-neutral-700">
									Información Adicional (opcional)
								</label>
								<textarea
									className="font-parkinsans w-full rounded-lg border border-neutral-300 px-4 py-3 text-sm focus:border-green-500 focus:ring-1 focus:ring-green-500 focus:outline-none"
									rows={3}
									placeholder="Información adicional relevante"
									value={formData.informacionAdicional}
									onChange={(e) => {
										setFormData({
											...formData,
											informacionAdicional: e.target.value,
										})
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar solicitud
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
